import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { message } from 'antd';
import storeApi from '../../services/storeApi';
import inventoryApi from '../../services/inventoryApi';
import squareCatalogApi from '../../services/squareCatalogApi';
import useSquareIntegrated from '../../components/menu/square-shared/use-square-integrated';
import SquareReconnectStrip from '../../components/menu/square-shared/reconnect-strip';
import squareErrorToast from '../../components/menu/square-shared/squareErrorToast';
import PageHeader from '../../components/ui/page-header';
import EmptyState from '../../components/ui/empty-state';
import TWButton from '../../components/ui/tw-button';
import Notification from '../../components/notification/notification';
import { TableRowsSkeleton } from '../../components/skeleton/skeleton';
import InventoryFilters from './components/inventory-filters';
import InventoryTable from './components/inventory-table';
import {
  buildInventoryListQuery,
  validateInventoryDraftRow,
  diffInventoryDraft,
  applyClientStatusFilter,
} from './inventory-utils';

const buildDraftFromCategories = (categories) => {
  const draft = {};
  categories.forEach((cat) =>
    (cat.items || []).forEach((item) => {
      draft[item.itemId] = {
        trackingEnabled: !!item.trackingEnabled,
        quantity: item.quantity ?? '',
        lowStockThreshold: item.lowStockThreshold ?? '',
      };
    }),
  );
  return draft;
};

const Inventory = () => {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const permissions = useSelector((state) => state.authReducer.rolepermissions);
  const canEdit = !!permissions?.includes('inventory-upsert');
  const { integrated: squareIntegrated } = useSquareIntegrated();

  const [storeList, setStoreList] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [categoryList, setCategoryList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState({});
  const [errors, setErrors] = useState({});
  const [categoryId, setCategoryId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState({ stores: true, list: false, saving: false });
  const [notf, setNotf] = useState({ show: false, type: 'error', message: '' });
  const [pullingInventory, setPullingInventory] = useState(false);
  const [inventorySummary, setInventorySummary] = useState(null);

  // Debounce the search input.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Load stores for the company; default to the first one.
  useEffect(() => {
    let active = true;
    const loadStores = async () => {
      setLoading((p) => ({ ...p, stores: true }));
      try {
        // storeApi.list already prepends `PageSize=1000&`, so do NOT pass pageSize again.
        const res = await storeApi.list(
          `companyid=${globalCompany?.id}&isdeleted=false`,
        );
        if (!active) return;
        const stores = res.data?.data || [];
        setStoreList(stores);
        setStoreId((prev) => prev || stores[0]?.id || '');
      } finally {
        if (active) setLoading((p) => ({ ...p, stores: false }));
      }
    };
    if (globalCompany?.id) loadStores();
    return () => {
      active = false;
    };
  }, [globalCompany?.id]);

  // Load categories whenever the store changes.
  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      try {
        const res = await inventoryApi.categories(`?storeid=${storeId}`);
        if (active) setCategoryList(res.data?.data || []);
      } catch (e) {
        if (active) setCategoryList([]);
      }
    };
    if (storeId) loadCategories();
    return () => {
      active = false;
    };
  }, [storeId]);

  // Load the grouped item list on store/filter/search changes.
  useEffect(() => {
    let active = true;
    const loadList = async () => {
      setLoading((p) => ({ ...p, list: true }));
      try {
        const query = buildInventoryListQuery({
          storeid: storeId,
          companyid: globalCompany?.id,
          categoryid: categoryId,
          search: debouncedSearch,
          statusFilter,
        });
        const res = await inventoryApi.list(query);
        if (!active) return;
        const cats = res.data?.data?.categories || [];
        setCategories(cats);
        setDraft(buildDraftFromCategories(cats));
        setErrors({});
      } catch (error) {
        if (active)
          setNotf({ show: true, type: 'error', message: 'Failed to load inventory.' });
      } finally {
        if (active) setLoading((p) => ({ ...p, list: false }));
      }
    };
    if (storeId) loadList();
    return () => {
      active = false;
    };
  }, [storeId, categoryId, statusFilter, debouncedSearch, globalCompany?.id]);

  const onChangeRow = (itemId, field, value) => {
    setDraft((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));
  };

  const onDiscard = () => {
    setDraft(buildDraftFromCategories(categories));
    setErrors({});
  };

  const dirtyUpdates = useMemo(
    () => diffInventoryDraft(categories, draft),
    [categories, draft],
  );
  const dirtyCount = dirtyUpdates.length;
  const dirtyIds = useMemo(
    () => new Set(dirtyUpdates.map((u) => u.itemId)),
    [dirtyUpdates],
  );

  // Client-side status filter as a safety net (works even if the server does
  // not honor statusFilter). Keyed on draft too, so toggling tracking live
  // re-evaluates filters 1–4 without a re-fetch.
  const visibleCategories = useMemo(
    () => applyClientStatusFilter(categories, statusFilter, draft),
    [categories, statusFilter, draft],
  );

  // Re-fetch the grouped item list for the current store/filter/search
  // selection. Shared by the Save flow and the Square inventory pull.
  const reloadList = async () => {
    const query = buildInventoryListQuery({
      storeid: storeId,
      companyid: globalCompany?.id,
      categoryid: categoryId,
      search: debouncedSearch,
      statusFilter,
    });
    const res = await inventoryApi.list(query);
    const cats = res.data?.data?.categories || [];
    setCategories(cats);
    setDraft(buildDraftFromCategories(cats));
  };

  const onSave = async () => {
    // Validate every dirty row; collect inline errors.
    const updates = diffInventoryDraft(categories, draft);
    const nextErrors = {};
    updates.forEach((u) => {
      const rowErrors = validateInventoryDraftRow(u);
      if (Object.keys(rowErrors).length) nextErrors[u.itemId] = rowErrors;
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setNotf({ show: true, type: 'error', message: 'Please fix the highlighted rows.' });
      return;
    }
    setLoading((p) => ({ ...p, saving: true }));
    try {
      await inventoryApi.bulkUpdate({ storeid: storeId, items: updates });
      setNotf({ show: true, type: 'success', message: 'Inventory updated' });
      await reloadList();
    } catch (error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        'Failed to update inventory.';
      setNotf({ show: true, type: 'error', message: apiMessage });
    } finally {
      setLoading((p) => ({ ...p, saving: false }));
    }
  };

  const onClickPullInventory = async () => {
    try {
      setPullingInventory(true);
      setInventorySummary(null);
      const res = await squareCatalogApi.pullInventory({
        companyid: globalCompany.id,
      });
      setInventorySummary(res.data.data);
      message.success('Inventory pulled');
      await reloadList();
    } catch (err) {
      squareErrorToast(err);
    } finally {
      setPullingInventory(false);
    }
  };

  if (!permissions?.includes('inventory-list')) {
    return (
      <div className="px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <div className="mx-auto max-w-5xl">
          <EmptyState
            title="Access denied"
            description="You do not have permission to view inventory."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[92vh] flex-col px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto flex w-full min-h-0 max-w-5xl flex-1 flex-col space-y-6">
        <PageHeader
          title="Inventory"
          description="Track stock for menu items in the selected store."
          actions={
            squareIntegrated ? (
              <TWButton
                variant="default"
                size="sm"
                label={pullingInventory ? 'Pulling…' : 'Pull from Square'}
                disabled={pullingInventory}
                onClick={onClickPullInventory}
              />
            ) : null
          }
        />
        <SquareReconnectStrip />
        <InventoryFilters
          storeList={storeList}
          storesLoading={loading.stores}
          storeId={storeId}
          onStoreChange={setStoreId}
          categoryList={categoryList}
          categoryId={categoryId}
          onCategoryChange={setCategoryId}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          search={search}
          onSearchChange={setSearch}
        />
        {inventorySummary && (
          <p className="text-xs text-gray-600 dark:text-slate-300">
            Inventory pulled — {inventorySummary.updated} items updated across{' '}
            {inventorySummary.storescovered} stores.
            {inventorySummary.nottrackedmapped > 0 && (
              <>
                {' '}
                {inventorySummary.nottrackedmapped} mapped items aren't tracked
                in Thisway — enable tracking below.
              </>
            )}
          </p>
        )}
        <div className="min-h-0 flex-1">
          {loading.list ? (
            <TableRowsSkeleton count={8} />
          ) : visibleCategories.length === 0 ? (
            <EmptyState title="No items" description="No menu items match these filters." />
          ) : (
            <InventoryTable
              categories={visibleCategories}
              draft={draft}
              errors={errors}
              onChangeRow={onChangeRow}
              canEdit={canEdit}
              dirtyIds={dirtyIds}
            />
          )}
        </div>

        {canEdit && dirtyCount > 0 ? (
          <div className="fixed bottom-6 left-1/2 z-40 -translate-x-1/2">
            <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white/95 px-4 py-2 shadow-lg backdrop-blur dark:border-slate-700 dark:bg-slate-800/95">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}
              </span>
              <TWButton
                variant="ghost"
                size="sm"
                pill
                label="Discard"
                disabled={loading.saving}
                onClick={onDiscard}
              />
              <TWButton
                variant="primary"
                size="sm"
                pill
                loading={loading.saving}
                label="Save changes"
                onClick={onSave}
              />
            </div>
          </div>
        ) : null}

        <Notification
          display={notf.show}
          type={notf.type}
          message={notf.message}
          onClose={() => setNotf((prev) => ({ ...prev, show: false }))}
        />
      </div>
    </div>
  );
};

export default Inventory;
